import React, { useState, useRef, useImperativeHandle, useEffect } from 'react';
import styled from 'styled-components';

export interface MultiSelectOption {
  value: string;
  text: string;
  key?: string;
}

const StyledWrapper = styled.div`
  width: 100%;
  margin: 8px 0;

  .multi-select-control {
    position: relative;
    width: 100%;
  }

  .multi-select-field {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    width: 100%;
    min-height: 42px;
    padding: 6px 34px 6px 12px;
    border: 1px solid #c4c4c4;
    border-radius: 4px;
    background: #fff;
    font-size: 1rem;
    font-family: inherit;
    box-sizing: border-box;
    cursor: pointer;
    transition: border-color 0.15s ease;
  }

  .multi-select-field.is-open,
  .multi-select-field:focus-visible {
    border-color: #0069ff;
    outline: none;
  }

  .multi-select-placeholder {
    color: #9ca3af;
  }

  .multi-select-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    max-width: 100%;
    padding: 2px 6px 2px 8px;
    border-radius: 4px;
    background: #eef0f3;
    font-size: 0.875em;
    line-height: 1.6;
  }

  .multi-select-chip-remove {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    padding: 0;
    border: none;
    border-radius: 3px;
    background: transparent;
    color: #6b7280;
    font-size: 13px;
    font-weight: 600;
    line-height: 1;
    cursor: pointer;
    user-select: none;
  }

  .multi-select-chip-remove:hover {
    background: #dfe3e8;
    color: #111827;
  }

  .multi-select-arrow {
    position: absolute;
    top: 50%;
    right: 12px;
    width: 8px;
    height: 8px;
    margin-top: -6px;
    border-right: 1.6px solid #6b7280;
    border-bottom: 1.6px solid #6b7280;
    transform: rotate(45deg);
    pointer-events: none;
  }

  .multi-select-menu {
    position: absolute;
    z-index: 20;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    max-height: 220px;
    overflow-y: auto;
    padding: 4px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background: #fff;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
    list-style: none;
    margin: 0;
  }

  .multi-select-menu li {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 10px;
    border-radius: 4px;
    font-size: 0.9375em;
    cursor: pointer;
  }

  .multi-select-menu li:hover {
    background: #f3f4f6;
  }

  .multi-select-menu li.is-selected {
    color: #4f46e5;
    font-weight: 600;
  }

  .multi-select-menu input[type='checkbox'] {
    width: 15px;
    height: 15px;
    margin: 0;
    accent-color: #4f46e5;
    pointer-events: none;
  }

  &.has-error .multi-select-field {
    border-color: #d32f2f;
  }

  .multi-select-error-text {
    color: #d32f2f;
    font-size: 0.75rem;
    margin-top: 3px;
  }
`;

interface MultiSelectData {
  field_name?: string;
  required?: boolean;
  options?: MultiSelectOption[];
  placeholder?: string;
  value?: string | string[];
}

interface MultiSelectProps {
  data: MultiSelectData;
  onChange?: (value: Record<string, string>) => void;
  read_only?: boolean;
  mutable?: boolean;
  defaultValue?: string | string[];
}

const toValueList = (raw?: string | string[]): string[] => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  return raw
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);
};

const MultiSelectInput = React.forwardRef<HTMLInputElement, MultiSelectProps>(
  (props, ref) => {
    const {
      field_name,
      required,
      options = [],
      placeholder,
      value: initialValue,
    } = props.data || {};

    const isLiveForm = props.mutable !== false;

    const [selected, setSelected] = useState<string[]>(
      toValueList(props.defaultValue ?? initialValue),
    );
    const [open, setOpen] = useState(false);
    const [touched, setTouched] = useState(false);
    const hiddenInputRef = useRef<HTMLInputElement>(null);
    const controlRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => hiddenInputRef.current as HTMLInputElement);

    useEffect(() => {
      if (!open) return;
      const onDocClick = (event: MouseEvent) => {
        if (!controlRef.current?.contains(event.target as Node)) {
          setOpen(false);
          setTouched(true);
        }
      };
      document.addEventListener('mousedown', onDocClick);
      return () => document.removeEventListener('mousedown', onDocClick);
    }, [open]);

    const showError = isLiveForm && touched && required && selected.length === 0;

    const emitChange = (next: string[]) => {
      setSelected(next);
      if (props.onChange && field_name) {
        props.onChange({ [field_name]: next.join(',') });
      }
    };

    const toggle = (value: string) => {
      emitChange(
        selected.includes(value)
          ? selected.filter((v) => v !== value)
          : [...selected, value],
      );
    };

    const chips = isLiveForm
      ? options.filter((o) => selected.includes(o.value))
      : options;

    return (
      <StyledWrapper className={showError ? 'has-error' : ''}>
        <div className="multi-select-control" ref={controlRef}>
          <div
            className={`multi-select-field${open ? ' is-open' : ''}`}
            role="button"
            tabIndex={isLiveForm && !props.read_only ? 0 : -1}
            onClick={() => {
              if (!isLiveForm || props.read_only) return;
              setOpen((v) => !v);
            }}
            onKeyDown={(e) => {
              if (!isLiveForm || props.read_only) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setOpen((v) => !v);
              } else if (e.key === 'Escape') {
                setOpen(false);
              }
            }}
          >
            {chips.length === 0 && (
              <span className="multi-select-placeholder">
                {placeholder || 'Select…'}
              </span>
            )}
            {chips.map((option) => (
              <span className="multi-select-chip" key={option.value}>
                {option.text}
                {isLiveForm && !props.read_only && (
                  <span
                    className="multi-select-chip-remove"
                    role="button"
                    tabIndex={0}
                    aria-label={`Remove ${option.text}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      emitChange(selected.filter((v) => v !== option.value));
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        emitChange(selected.filter((v) => v !== option.value));
                      }
                    }}
                  >
                    ×
                  </span>
                )}
              </span>
            ))}
            <span className="multi-select-arrow" />
          </div>

          {open && (
            <ul className="multi-select-menu">
              {options.map((option) => {
                const isSelected = selected.includes(option.value);
                return (
                  <li
                    key={option.value}
                    className={isSelected ? 'is-selected' : ''}
                    onClick={() => toggle(option.value)}
                  >
                    <input type="checkbox" readOnly checked={isSelected} />
                    {option.text}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {showError && (
          <div className="multi-select-error-text">This field is required.</div>
        )}
        <input
          ref={hiddenInputRef}
          type="hidden"
          name={field_name}
          value={selected.join(',')}
        />
      </StyledWrapper>
    );
  },
);

MultiSelectInput.displayName = 'MultiSelectInput';

export default MultiSelectInput;
